// @ts-ignore
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import axios from 'axios';
import { format } from 'date-fns';
import { checkAuth } from '../../utils/authUtils'; // Adjusted import path
import DownloadIcon from '@mui/icons-material/Download';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { motion } from 'framer-motion';
import jsPDF from 'jspdf';

// Create API base URL that handles whether API_URL already includes /api or not
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
const API_BASE = API_URL.endsWith('/api') ? API_URL.slice(0, -4) : API_URL;

// Helper function to recursively render object data for PDF
const renderObjectRecursivePdf = (doc: jsPDF, obj: any, xOffset: number, currentY: { y: number }, lineHeight: number, pageHeight: number, pageWidth: number, indentLevel = 0) => {
  const indent = '  '.repeat(indentLevel);
  for (const key in obj) {
    if (obj.hasOwnProperty(key)) {
      if (currentY.y > pageHeight) {
        doc.addPage();
        currentY.y = 20;
      }
      const value = obj[key];
      const formattedKey = key.replace(/([A-Z])/g, ' $1').replace(/^./, (str) => str.toUpperCase());

      if (isStoredFile(value)) { // Handle StoredFile objects specifically
        doc.text(`${indent}${formattedKey}: ${value.originalname || value.filename} (Viewable/downloadable separately)`, xOffset, currentY.y, { maxWidth: pageWidth - xOffset - 5 });
        currentY.y += lineHeight;
      } else if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
        doc.text(`${indent}${formattedKey}:`, xOffset, currentY.y, { maxWidth: pageWidth - xOffset - 5 });
        currentY.y += lineHeight;
        renderObjectRecursivePdf(doc, value, xOffset + (indentLevel < 1 ? 5 : 0), currentY, lineHeight, pageHeight, pageWidth, indentLevel + 1); // Indent further for nested objects
      } else if (Array.isArray(value)) {
        doc.text(`${indent}${formattedKey}:`, xOffset, currentY.y);
        currentY.y += lineHeight;
        value.forEach((item, index) => {
          if (currentY.y > pageHeight) {
            doc.addPage();
            currentY.y = 20;
          }
          if (typeof item === 'object' && item !== null) {
            doc.text(`${indent}  Item ${index + 1}:`, xOffset, currentY.y);
            currentY.y += lineHeight;
            renderObjectRecursivePdf(doc, item, xOffset + 5, currentY, lineHeight, pageHeight, pageWidth, indentLevel + 2);
          } else {
            doc.text(`${indent}  - ${String(item)}`, xOffset + 5, currentY.y, { maxWidth: pageWidth - xOffset - 10 });
            currentY.y += lineHeight;
          }
        });
      } else {
        doc.text(`${indent}${formattedKey}: ${String(value)}`, xOffset, currentY.y, { maxWidth: pageWidth - xOffset - 5 });
        currentY.y += lineHeight;
      }
    }
  }
};

// Styled Components (can be expanded and refined)
const ViewContainer = styled.div`
  padding: 20px;
  max-width: 960px;
  margin: 30px auto;
  background-color: #f9fafb; // Lighter background for the page
  border-radius: 12px;
  font-family: 'Inter', sans-serif; // Assuming Inter or a similar modern font is available

  @media (min-width: 768px) {
    padding: 40px;
  }
`;

const Card = styled.div`
  background: #ffffff;
  border-radius: 10px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  padding: 25px;
  margin-bottom: 30px;

  &:last-of-type {
    margin-bottom: 0;
  }
`;

const PageTitle = styled.h1`
  font-size: 2.25rem; // 36px
  font-weight: 700;
  color: #1f2937; // Darker gray
  text-align: center;
  margin-bottom: 40px;
`;

const SectionTitle = styled.h2`
  font-size: 1.5rem; // 24px
  font-weight: 600;
  color: #374151; // Medium gray
  margin-bottom: 20px;
  padding-bottom: 10px;
  border-bottom: 2px solid #e5e7eb; // Solid, slightly thicker border
`;

const DetailItem = styled.div`
  display: grid;
  grid-template-columns: 200px 1fr;
  gap: 15px;
  padding: 12px 0;
  font-size: 0.95rem; // Slightly smaller for more content
  border-bottom: 1px solid #f3f4f6; // Very light separator for items

  &:last-child {
    border-bottom: none;
  }
`;

const DetailLabel = styled.span`
  font-weight: 500; // Medium weight
  color: #6b7280; // Lighter gray for label
`;

const DetailValue = styled.span`
  color: #1f2937; // Darker gray for value
  word-break: break-word;
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 200px;
  font-size: 18px;
  color: #718096;
`;

const ErrorMessage = styled.div`
  color: #e53e3e;
  background-color: #fff5f5;
  border: 1px solid #fc8181;
  padding: 15px;
  border-radius: 6px;
  text-align: center;
  margin-bottom: 20px;
`;

const BackButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 12px 24px;
  background: linear-gradient(135deg, #4a6cf7 0%, #6a82fb 100%);
  color: white;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-size: 1rem; // 16px
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-top: 0; /* Moved to ActionsWrapper */
  box-shadow: 0 4px 10px rgba(74, 108, 247, 0.3);
  transition: all 0.3s ease;

  svg {
    margin-right: 8px;
    width: 20px;
    height: 20px;
  }

  &:hover {
    background: linear-gradient(135deg, #3a56d4 0%, #5a72e9 100%);
    box-shadow: 0 6px 15px rgba(74, 108, 247, 0.4);
    transform: translateY(-2px);
  }

  &:active {
    transform: translateY(0);
    box-shadow: 0 2px 5px rgba(74, 108, 247, 0.3);
  }
`;




// Interface for file objects stored within formData
interface StoredFile {
  filename: string;
  originalname?: string; // If you store originalname, add it here
  // Add any other properties you store for files, e.g., mimetype, size
}

// Styled components for floating action buttons
const FloatingActionContainer = styled(motion.div)`
  position: fixed;
  bottom: 30px;
  right: 30px;
  display: flex;
  flex-direction: column; // Stack buttons vertically
  gap: 15px; // Space between buttons
  z-index: 1000;
`;

const FloatingActionButtonStyled = styled(motion.button)`
  background: linear-gradient(135deg, #4a6cf7 0%, #6a82fb 100%);
  color: white;
  border: none;
  border-radius: 50px; // Pill shape
  padding: 12px 20px;
  font-size: 16px;
  font-weight: 500;
  display: flex;
  align-items: center;
  justify-content: center; // Center content for consistent look
  gap: 8px;
  box-shadow: 0 6px 12px rgba(0, 0, 0, 0.2);
  cursor: pointer;
  transition: all 0.3s ease;
  min-width: 180px; // Ensure buttons have a decent minimum width

  &:hover {
    background: linear-gradient(135deg, #3a56d4 0%, #5a72e9 100%);
    box-shadow: 0 8px 16px rgba(0, 0, 0, 0.25);
    transform: translateY(-2px);
  }

  svg {
    font-size: 20px;
  }

  &:disabled {
    background: #ccc;
    cursor: not-allowed;
    box-shadow: none;
    transform: none;
  }
`;

// Type guard to check if an object is a StoredFile
function isStoredFile(value: any): value is StoredFile {
  return typeof value === 'object' && value !== null && typeof value.filename === 'string';
}

interface ApplicationData {
  applicationId: string;
  email: string;
  formData: any; // Consider defining a more specific type for formData
  status: string;
  isComplete: boolean;
  isSubmitted: boolean;
  createdAt: string;
  updatedAt: string;
  // Add other fields as necessary
}

const ApplicationView: React.FC = () => {
  const { applicationId } = useParams<{ applicationId: string }>();
  const navigate = useNavigate();
  const [application, setApplication] = useState<ApplicationData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const handleDownloadApplication = (app: ApplicationData) => {
    if (!app) return;

    const doc = new jsPDF();
    // Create a deep copy for manipulation, excluding Mongoose internals if they exist at this stage
    const dataToDownload = JSON.parse(JSON.stringify(app)); 
    if (dataToDownload._id) delete dataToDownload._id;
    if (dataToDownload.__v) delete dataToDownload.__v;

    let applicantNamePart = 'details';
    if (app.formData && app.formData.fullName) {
      applicantNamePart = app.formData.fullName.replace(/[^a-z0-9]/gi, '_').toLowerCase();
    } else if (app.email) {
      applicantNamePart = app.email.split('@')[0].replace(/[^a-z0-9]/gi, '_').toLowerCase();
    }
    const filename = `application_${applicantNamePart}_${app.applicationId}.pdf`;

    let yPosition = 20;
    const lineHeight = 7;
    const pageHeight = doc.internal.pageSize.height - 20; // Margin at bottom
    const pageWidth = doc.internal.pageSize.width;
    const xOffset = 10;

    doc.setFontSize(16);
    doc.text('Application Details', pageWidth / 2, yPosition, { align: 'center' });
    yPosition += lineHeight * 2;
    doc.setFontSize(12);

    for (const key in dataToDownload) {
      if (dataToDownload.hasOwnProperty(key)) {
        if (yPosition > pageHeight) {
          doc.addPage();
          yPosition = 20; // Reset Y position for new page
        }
        let value = dataToDownload[key];
        const formattedKey = key.replace(/([A-Z])/g, ' $1').replace(/^./, (str) => str.toUpperCase());
        
        let yPosRef = { y: yPosition };

        if (key === 'formData' || key === 'documents' || (typeof value === 'object' && value !== null && !Array.isArray(value) && key !== 'history')) {
          if (yPosRef.y > pageHeight) { doc.addPage(); yPosRef.y = 20; }
          doc.text(`${formattedKey}:`, xOffset, yPosRef.y);
          yPosRef.y += lineHeight;
          renderObjectRecursivePdf(doc, value, xOffset + 5, yPosRef, lineHeight, pageHeight, pageWidth, 0);
        } else if (key === 'history' && Array.isArray(value)) {
          if (yPosRef.y > pageHeight) { doc.addPage(); yPosRef.y = 20; }
          doc.text(`${formattedKey}:`, xOffset, yPosRef.y);
          yPosRef.y += lineHeight;
          (value as Array<any>).forEach((entry, index) => {
            if (yPosRef.y > pageHeight) { doc.addPage(); yPosRef.y = 20; }
            const entryText = `  Entry ${index + 1}: Status - ${entry.status}, Changed At - ${new Date(entry.changedAt || entry.timestamp).toLocaleString()}` + (entry.editor ? `, By - ${entry.editor}` : '') + (entry.details ? ` (${entry.details})` : '');
            doc.text(entryText, xOffset + 5, yPosRef.y, { maxWidth: pageWidth - xOffset - 15 });
            yPosRef.y += lineHeight;
          });
        } else if (!Array.isArray(value)) { // Simple key-value pairs
          if (yPosRef.y > pageHeight) { doc.addPage(); yPosRef.y = 20; }
          doc.text(`${formattedKey}: ${String(value)}`, xOffset, yPosRef.y, { maxWidth: pageWidth - xOffset - 10 });
          yPosRef.y += lineHeight;
        }
        yPosition = yPosRef.y; // Update main yPosition from the reference
      }
    }
    doc.save(filename);
  };


  useEffect(() => {
    const verifyAndFetch = async () => {
      try {
        const { authenticated } = await checkAuth();
        if (!authenticated) {
          navigate('/'); // Redirect to login or home if not authenticated
          return;
        }

        if (!applicationId) {
          setError('Application ID is missing.');
          setIsLoading(false);
          return;
        }

        axios.defaults.withCredentials = true;
        const response = await axios.get(`${API_BASE}/api/applications/${applicationId}`);
        
        if (response.data.success) {
          setApplication(response.data.application);
        } else {
          setError(response.data.message || 'Failed to fetch application details.');
        }
      } catch (err: any) {
        console.error('Error fetching application details:', err);
        setError(err.response?.data?.message || 'An error occurred while fetching application details.');
      } finally {
        setIsLoading(false);
      }
    };

    verifyAndFetch();
  }, [applicationId, navigate]);

  if (isLoading) {
    return <LoadingContainer>Loading application details...</LoadingContainer>;
  }

  if (error) {
    return (
      <ViewContainer>
        <ErrorMessage>{error}</ErrorMessage>
        <BackButton onClick={() => navigate(-1)}>Go Back</BackButton>
      </ViewContainer>
    );
  }

  if (!application) {
    return (
      <ViewContainer>
        <ErrorMessage>Application not found.</ErrorMessage>
        <BackButton onClick={() => navigate(-1)}>Go Back</BackButton>
      </ViewContainer>
    );
  }

  // Helper to render form data details (can be customized)
  const renderFormData = () => {
    if (!application.formData || typeof application.formData !== 'object') {
      return <DetailItem><DetailValue>No additional form data available.</DetailValue></DetailItem>;
    }
    return Object.entries(application.formData).map(([key, value]) => {
      // Use the type guard to check for StoredFile
      if (isStoredFile(value)) {
        const filePath = value.filename.startsWith('/uploads/') ? value.filename : `/uploads/${value.filename}`;
        const fileUrl = `${API_URL}${filePath}`; // Construct file URL
        return (
          <DetailItem key={key}>
            <DetailLabel>{key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}:</DetailLabel>
            <DetailValue>
              <a href={fileUrl} target="_blank" rel="noopener noreferrer" style={{ color: '#4a6cf7', textDecoration: 'underline' }}>
                {value.originalname || value.filename} {/* Show originalname if available, else filename */}
              </a>
            </DetailValue>
          </DetailItem>
        );
      }      
      if (typeof value !== 'object' && value !== null && typeof value !== 'function') { // Display simple values, exclude functions
        return (
          <DetailItem key={key}>
            <DetailLabel>{key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}:</DetailLabel>
            <DetailValue>{String(value)}</DetailValue>
          </DetailItem>
        );
      }
      return null; // Skip complex objects not handled
    });
  };

  return (
    <ViewContainer>
      <PageTitle>Application Details</PageTitle>
      
      <Card> {/* Wrap sections in Cards for better visual separation */} 
        <SectionTitle>General Information</SectionTitle>
        <DetailItem>
          <DetailLabel>Application ID:</DetailLabel>
          <DetailValue>{application.applicationId}</DetailValue>
        </DetailItem>
        <DetailItem>
          <DetailLabel>Email:</DetailLabel>
          <DetailValue>{application.email}</DetailValue>
        </DetailItem>
        <DetailItem>
          <DetailLabel>Status:</DetailLabel>
          <DetailValue>{application.status}</DetailValue>
        </DetailItem>
        <DetailItem>
          <DetailLabel>Created At:</DetailLabel>
          <DetailValue>{format(new Date(application.createdAt), 'PPPppp')}</DetailValue>
        </DetailItem>
        <DetailItem>
          <DetailLabel>Last Updated:</DetailLabel>
          <DetailValue>{format(new Date(application.updatedAt), 'PPPppp')}</DetailValue>
        </DetailItem>
      </Card>

      <Card>
        <SectionTitle>Form Data</SectionTitle>
        {renderFormData()}
      </Card>
      
      {/* Placeholder for future Uploaded Documents card 
      { (application.formData.drivingLicense || otherFileFieldsPresent) && 
        <Card>
          <SectionTitle>Uploaded Documents</SectionTitle>
          {application.formData.drivingLicense && 
            <DetailItem>
              <DetailLabel>Driving License:</DetailLabel>
              <DetailValue>
                <a href={`/api/files/${application.formData.drivingLicense.filename}`} target="_blank" rel="noopener noreferrer">
                  {application.formData.drivingLicense.filename}
                </a>
              </DetailValue>
            </DetailItem>}
          // ... other file links similarly ...
        </Card>
      }*/}

      {application && (
        <FloatingActionContainer
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ staggerChildren: 0.1, delayChildren: 0.2 }} // Stagger animation for children
        >
          <FloatingActionButtonStyled
            onClick={() => handleDownloadApplication(application)}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            disabled={!application} // Disable if application data is not loaded
          >
            <DownloadIcon />
            Download PDF
          </FloatingActionButtonStyled>
          <FloatingActionButtonStyled
            onClick={() => navigate(-1)}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <ArrowBackIcon />
            Go Back
          </FloatingActionButtonStyled>
        </FloatingActionContainer>
      )}
    </ViewContainer>
  );
};

export default ApplicationView;
